/**
 * Shared plumbing for the async-HTTP-API REST clients (`sessionApi`,
 * `userFilesApi`, `workflowsApi`, `jobsApi`, …) — U19 consolidation. Each
 * client keeps its own endpoint paths, response types, and user-friendly
 * error copy; what they share lives here exactly once:
 *
 *  - the Cognito ACCESS-token bearer header (the async API's JWT authorizer
 *    matches on `client_id`; `signedFetch` is only for IAM-signed routes)
 *  - the never-throws response-body reader used for error logging
 *  - `requestJson` / `requestNoContent` — fetch + status check + console
 *    detail log + throw of the caller's friendly message as an
 *    `ApiRequestError` carrying the status, the idiom every client repeated
 */

import { getAccessToken } from '../auth/accessToken.ts';
import { requestTimeout } from '../config/requestTimeout.ts';
import { ApiRequestError } from './apiRequestError.ts';

export async function authHeader(): Promise<Record<string, string>> {
  return { Authorization: `Bearer ${await getAccessToken()}` };
}

/** Read a response body for diagnostics without ever throwing. */
export async function safeText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return '<unreadable body>';
  }
}

export interface RequestJsonOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  /** Tag for the console diagnostic line, e.g. "sessionApi". */
  readonly logTag: string;
  /** Thrown to the caller (and rendered to the user) on a non-OK response. */
  readonly friendlyError: string;
}

/** Send the request; anything but an OK status throws `ApiRequestError`. */
async function send(url: string, options: RequestJsonOptions): Promise<Response> {
  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers: {
      ...(await authHeader()),
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    signal: requestTimeout(),
  });
  if (!response.ok) {
    console.error(
      `[${options.logTag}] ${options.method ?? 'GET'} failed (${response.status})`,
      await safeText(response),
    );
    throw new ApiRequestError(options.friendlyError, `http-${response.status}`);
  }
  return response;
}

/**
 * Authorized JSON request against the async API. Non-OK responses log the
 * status + body detail for debugging and throw an `ApiRequestError` whose
 * message is `friendlyError`.
 *
 * The body is required. An empty one used to resolve to `undefined` typed as
 * `T`, so a caller reading `body.runs` threw a bare `TypeError` that was
 * recorded exactly like a dropped connection. A route that answers with no
 * content is called through `requestNoContent` instead.
 */
export async function requestJson<T>(url: string, options: RequestJsonOptions): Promise<T> {
  const response = await send(url, options);
  const text = await response.text();
  if (text === '') {
    console.error(
      `[${options.logTag}] ${options.method ?? 'GET'} returned an empty body (${response.status})`,
    );
    throw new ApiRequestError(options.friendlyError, 'empty-body');
  }
  return JSON.parse(text) as T;
}

/** Authorized request whose response has no content to read — a DELETE's 204. */
export async function requestNoContent(url: string, options: RequestJsonOptions): Promise<void> {
  await send(url, options);
}
